<template>
  <main class="app-content">
    <div class="app-title">
      <div class="row">
        <div class="col-md">
          <h1><i class="fa fa-dashboard"></i> {{ $t('overview') }}</h1>
        </div>
        <div class="col-md">
          <time-range-picker :model="filterFormModel" :label="$t('time')" @change="load" :defaultShortcut="0"></time-range-picker>
        </div>
      </div>
    </div>

    <!-- Row 1: Summary cards -->
    <div class="row" v-if="$store.state.user.role !== 'agency'">
      <div class="col-md-6 col-lg-3">
        <div class="widget-small info coloured-icon">
          <div class="icon"><font-awesome-icon icon="handshake"/></div>
          <div class="info">
            <h4>Client</h4>
            <p><span>{{ $t('new') }}: </span><b>{{ count.client }}</b></p>
            <p><span>{{ $t('online') }}: </span><b>{{ count.onlineClient }}</b></p>
          </div>
        </div>
      </div>
      <div class="col-md-6 col-lg-3">
        <div class="widget-small info coloured-icon">
          <div class="icon"><i class="fa fa-user-plus"></i></div>
          <div class="info">
            <h4>{{ $t('user') }}</h4>
            <p><span>{{ $t('new') }}: </span><b>{{ count.user }}</b></p>
            <p><span>{{ $t('online') }}: </span><b>{{ count.onlineUser }}</b></p>
          </div>
        </div>
      </div>
      <div class="col-md-6 col-lg-3">
        <router-link to="/report/call">
          <div class="widget-small danger coloured-icon">
            <div class="icon"><font-awesome-icon icon="headphones-alt"/></div>
            <div class="info">
              <h4>{{ $t('call') }}</h4>
              <p><span>{{ $t('total') }}: </span><b>{{ count.callTotal }}</b></p>
              <p><span>{{ $t('connected') }}: </span><b>{{ count.callSuccess }}</b></p>
            </div>
          </div>
        </router-link>
      </div>
      <div class="col-md-6 col-lg-3">
        <div class="widget-small danger coloured-icon">
          <div class="icon"><font-awesome-icon icon="dollar-sign"/></div>
          <div class="info">
            <h4>{{ $t('finance') }}</h4>
            <p><span>{{ $t('deposit') }}: </span><b>{{ count.deposit | numFormat }}</b></p>
            <p><span>{{ $t('spent') }}: </span><b>{{ count.spent | numFormat }}</b></p>
          </div>
        </div>
      </div>
    </div>

    <!-- Row 2: Trend chart -->
    <div class="row mb-4" v-if="$store.state.user.role !== 'agency'">
      <div class="col-12">
        <div class="card p-3">
          <h5>{{ $t('trend') }}</h5>
          <apexchart v-if="trendOptions.xaxis.categories.length" type="line" height="280" :options="trendOptions" :series="trendSeries"/>
          <p v-else class="text-muted text-center py-5">{{ $t('noData') }}</p>
        </div>
      </div>
    </div>

    <!-- Row 3: Messages + AI -->
    <div class="row mb-4" v-if="$store.state.user.role !== 'agency'">
      <div class="col-md-4">
        <div class="card p-3 h-100">
          <h5><font-awesome-icon icon="comments" class="text-primary"/> {{ $t('messages') }}</h5>
          <div v-if="extra.messages && extra.messages.length">
            <div v-for="m in extra.messages" :key="m._id" class="d-flex justify-content-between py-1 border-bottom">
              <span>{{ platformLabels[m._id] || m._id || 'Khác' }}</span>
              <b-badge variant="primary">{{ m.count | numFormat }}</b-badge>
            </div>
          </div>
          <p v-else class="text-muted text-center py-4">{{ $t('noData') }}</p>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card p-3 h-100">
          <h5><font-awesome-icon icon="robot" class="text-success"/> AI</h5>
          <div v-if="extra.ai">
            <div class="mb-2">
              <small class="text-muted">Agent (Chatbot)</small>
              <div class="d-flex justify-content-between">
                <span>{{ extra.ai.agent.total | numFormat }} tin</span>
                <span class="text-success">${{ extra.ai.agent.cost }}</span>
              </div>
              <div class="d-flex justify-content-between">
                <span>Lỗi: <span :class="extra.ai.agent.errorRate > 10 ? 'text-danger' : ''">{{ extra.ai.agent.errorRate }}%</span></span>
                <span>TB: {{ extra.ai.agent.avgDuration }}s</span>
              </div>
            </div>
            <hr class="my-2"/>
            <div>
              <small class="text-muted">Trợ lý (Nội bộ)</small>
              <div class="d-flex justify-content-between">
                <span>{{ extra.ai.assistant.total | numFormat }} câu</span>
                <span class="text-success">${{ extra.ai.assistant.cost }}</span>
              </div>
              <div>Lỗi: <span :class="extra.ai.assistant.errorRate > 10 ? 'text-danger' : ''">{{ extra.ai.assistant.errorRate }}%</span></div>
            </div>
          </div>
          <p v-else class="text-muted text-center py-4">{{ $t('noData') }}</p>
        </div>
      </div>
      <div class="col-md-4">
        <div class="card p-3 h-100">
          <h5><font-awesome-icon icon="rocket" class="text-warning"/> {{ $t('campaignsAlerts') }}</h5>
          <div v-if="extra.campaigns">
            <div class="d-flex justify-content-between py-1 border-bottom">
              <span>{{ $t('callCampaignActive') }}</span>
              <b-badge variant="warning">{{ extra.campaigns.callActive }}</b-badge>
            </div>
            <div class="d-flex justify-content-between py-1 border-bottom">
              <span>{{ $t('messageCampaignActive') }}</span>
              <b-badge variant="warning">{{ extra.campaigns.messageActive }}</b-badge>
            </div>
          </div>
          <div v-if="extra.alerts" class="mt-2">
            <div v-if="extra.alerts.lowCredit > 0" class="d-flex justify-content-between py-1 border-bottom">
              <span class="text-danger"><font-awesome-icon icon="exclamation-triangle"/> Credit thấp</span>
              <b-badge variant="danger">{{ extra.alerts.lowCredit }} client</b-badge>
            </div>
            <div v-if="extra.alerts.botErrors > 0" class="d-flex justify-content-between py-1 border-bottom">
              <span class="text-danger"><font-awesome-icon icon="exclamation-triangle"/> Bot lỗi</span>
              <b-badge variant="danger">{{ extra.alerts.botErrors }} bot</b-badge>
            </div>
            <div v-if="!extra.alerts.lowCredit && !extra.alerts.botErrors" class="text-success py-2">
              <font-awesome-icon icon="check-circle"/> {{ $t('noAlerts') }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Row 4: Top clients -->
    <div class="row" v-if="$store.state.user.role !== 'agency'">
      <div class="col-12">
        <div class="card p-3">
          <h5><font-awesome-icon icon="trophy" class="text-warning"/> {{ $t('topClients') }}</h5>
          <b-table v-if="extra.topClients && extra.topClients.length" :items="extra.topClients" :fields="topClientFields" small striped hover>
            <template #cell(revenue)="data">
              {{ data.value | numFormat }}
            </template>
          </b-table>
          <p v-else class="text-muted text-center py-3">{{ $t('noData') }}</p>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import TimeRangePicker from "../components/TimeRangePicker";

export default {
  components: { TimeRangePicker },
  data: function () {
    return {
      count: {},
      extra: {},
      filterFormModel: {},
      platformLabels: {
        facebook: 'Facebook',
        instagram: 'Instagram',
        zalo: 'Zalo OA',
        telegram: 'Telegram',
        whatsapp: 'WhatsApp',
        livechat: 'Live Chat',
        sms: 'SMS',
      },
      topClientFields: [
        { key: 'name', label: 'Khách hàng' },
        { key: 'domain', label: 'Domain' },
        { key: 'orders', label: 'Đơn hàng', class: 'text-right' },
        { key: 'revenue', label: 'Doanh thu', class: 'text-right' },
      ],
    };
  },
  computed: {
    trendOptions() {
      var trend = (this.extra.trend || []);
      return {
        chart: { type: 'line', stacked: false, toolbar: { show: false } },
        xaxis: { categories: trend.map(function (t) { return t.date; }) },
        yaxis: [
          { title: { text: 'Số lượng' } },
          { opposite: true, title: { text: 'Doanh thu' } },
        ],
        colors: ['#007bff', '#28a745', '#ffc107'],
        plotOptions: { bar: { columnWidth: '60%' } },
        stroke: { width: [0, 0, 2] },
      };
    },
    trendSeries() {
      var trend = (this.extra.trend || []);
      return [
        { name: 'Client mới', type: 'column', data: trend.map(function (t) { return t.clients || 0; }) },
        { name: 'Cuộc gọi', type: 'column', data: trend.map(function (t) { return t.calls || 0; }) },
        { name: 'Nạp tiền', type: 'line', data: trend.map(function (t) { return t.deposit || 0; }) },
      ];
    },
  },
  methods: {
    load() {
      this.getCount();
      this.getExtra();
    },
    getCount() {
      if (this.$store.state.user.role !== 'agency') {
        this.$bgService.get('/dashboard/count', { params: this.filterFormModel }).then(response => {
          if (response.data) {
            this.count = response.data;
          }
        });
      }
    },
    getExtra() {
      if (this.$store.state.user.role !== 'agency') {
        var params = {};
        if (this.filterFormModel.from) params.from = this.filterFormModel.from;
        if (this.filterFormModel.to) params.to = this.filterFormModel.to;
        this.$bgService.get('/dashboard/extra', { params: params }).then(response => {
          if (response.data) {
            this.extra = response.data;
          }
        });
      }
    },
  },
  mounted() {
    this.load();
    this.reloadTimerCount = setInterval(() => {
      this.getCount();
    }, 10000);
  },
  beforeDestroy: function () {
    if (this.reloadTimerCount !== null) {
      clearInterval(this.reloadTimerCount);
    }
  },
  i18n: {
    messages: {
      en: {
        overview: 'Overview',
        user: 'User',
        new: 'New',
        online: 'Online',
        call: 'Call',
        total: 'Total',
        connected: 'Connected',
        deposit: 'Deposit',
        spent: 'Spent',
        finance: 'Finance',
        trend: 'Trend',
        messages: 'Messages',
        campaignsAlerts: 'Campaigns & Alerts',
        callCampaignActive: 'Call campaigns active',
        messageCampaignActive: 'Message campaigns active',
        topClients: 'Top clients (revenue)',
        noData: 'No data',
        noAlerts: 'No alerts',
        time: 'Time',
      },
      vi: {
        overview: 'Tổng quan',
        user: 'Người dùng',
        new: 'Mới',
        online: 'Online',
        call: 'Cuộc gọi',
        total: 'Tổng',
        connected: 'Kết nối',
        deposit: 'Khách nạp',
        spent: 'Khách tiêu',
        finance: 'Tài chính',
        trend: 'Xu hướng',
        messages: 'Tin nhắn',
        campaignsAlerts: 'Chiến dịch & Cảnh báo',
        callCampaignActive: 'Chiến dịch gọi đang chạy',
        messageCampaignActive: 'Chiến dịch tin nhắn đang chạy',
        topClients: 'Top khách hàng (doanh thu)',
        noData: 'Chưa có dữ liệu',
        noAlerts: 'Không có cảnh báo',
        time: 'Thời gian',
      }
    }
  },
};
</script>
